import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProductionSidebar from "@/components/ProductionSidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { crewMembersQuery } from "@/lib/queries/productionQueries";
import { Loader2, Mail, CheckCircle2, RefreshCw, Pencil } from "lucide-react";
import { useSidebar } from "@/contexts/SidebarContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageTransition } from "@/components/PageTransition";
import { EditProductionFAB } from "@/components/EditProductionFAB";
import { format, parseISO } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface CrewMember {
  id: string;
  email: string;
  name: string;
  function: string;
  phone: string | null;
  event_date: string;
  user_id: string | null;
  invited_at: string | null;
  email_delivered_at: string | null;
  email_opened_at: string | null;
}

interface GroupedCrewMember {
  primaryRecord: CrewMember;
  allDates: string[];
  allIds: string[];
  // Function per event_date, captured as an ordered list so the table
  // can either collapse to one role (when all days agree) or render a
  // per-day breakdown (when the producer assigned different roles).
  rolesByDate: Array<{ event_date: string; function: string }>;
}

const ManageCrew = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { productionId: selectedBriefingId } = useParams();
  const { expanded } = useSidebar();
  const { session } = useAuth();

  useEffect(() => {
    if (!session) {
      navigate("/producer-login");
    }
  }, [session, navigate]);

  // Redirect if no production is selected
  useEffect(() => {
    if (!selectedBriefingId) {
      navigate("/dashboard");
    }
  }, [selectedBriefingId, navigate]);

  // Fetch ALL crew members for selected briefing (no deduplication in query)
  const { data: rawCrewMembers = [], isLoading: crewLoading } = useQuery({
    ...crewMembersQuery(selectedBriefingId),
    enabled: !!selectedBriefingId,
    select: (rows) => rows as CrewMember[],
  });

  // Group crew members by email and collect all their event dates
  const groupedCrewMembers = useMemo((): GroupedCrewMember[] => {
    const grouped = new Map<string, GroupedCrewMember>();

    rawCrewMembers.forEach((member) => {
      // Group by canonical identity. briefing_crew_id (post-migration
      // 20260504240100) survives email rename; lowercased+trimmed
      // email is the legacy fallback so `Foo@x.com` and `foo@x.com`
      // stay merged into one row instead of generating two
      // invitations on save.
      const bcId = (member as { briefing_crew_id?: string | null }).briefing_crew_id;
      const groupKey = bcId ? `bc:${bcId}` : `email:${(member.email ?? "").toLowerCase().trim()}`;
      const existing = grouped.get(groupKey);
      if (existing) {
        existing.allDates.push(member.event_date);
        existing.allIds.push(member.id);
        existing.rolesByDate.push({
          event_date: member.event_date,
          function: member.function,
        });
      } else {
        grouped.set(groupKey, {
          primaryRecord: member,
          allDates: [member.event_date],
          allIds: [member.id],
          rolesByDate: [{ event_date: member.event_date, function: member.function }],
        });
      }
    });

    // Sort dates for each grouped member
    grouped.forEach((group) => {
      group.allDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      group.rolesByDate.sort(
        (a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime(),
      );
    });

    return Array.from(grouped.values());
  }, [rawCrewMembers]);

  // Format multiple dates nicely
  const formatEventDates = (dates: string[]): string => {
    if (dates.length === 0) return "-";
    if (dates.length === 1) return format(parseISO(dates[0]), "d MMM yyyy");

    // Check if dates are consecutive
    const sortedDates = [...dates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const firstDate = parseISO(sortedDates[0]);
    const lastDate = parseISO(sortedDates[sortedDates.length - 1]);

    // If same month and year, show condensed format
    if (format(firstDate, "MMM yyyy") === format(lastDate, "MMM yyyy")) {
      const dayNums = sortedDates.map((d) => format(parseISO(d), "d"));
      return `${dayNums.join(", ")} ${format(firstDate, "MMM yyyy")}`;
    }

    // Otherwise show range
    return `${format(firstDate, "d MMM")} - ${format(lastDate, "d MMM yyyy")}`;
  };

  // Track loading state per crew member
  const [loadingInvites, setLoadingInvites] = useState<Set<string>>(new Set());

  // Send invitation mutation (single)
  const sendInviteMutation = useMutation({
    mutationFn: async (crewMemberId: string) => {
      setLoadingInvites((prev) => new Set(prev).add(crewMemberId));

      const { data, error } = await supabase.functions.invoke("send-crew-invite", {
        body: {
          crewMemberId,
          briefingId: selectedBriefingId,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, crewMemberId) => {
      setLoadingInvites((prev) => {
        const next = new Set(prev);
        next.delete(crewMemberId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["crew-members", selectedBriefingId] });
      toast({
        title: "Invitation sent",
        description: "The crew member has received an email",
      });
    },
    onError: (error, crewMemberId) => {
      setLoadingInvites((prev) => {
        const next = new Set(prev);
        next.delete(crewMemberId);
        return next;
      });
      toast({
        title: "Error",
        description: "Something went wrong while sending the invitation",
        variant: "destructive",
      });
    },
  });

  // Send invitations to all crew members (using primary record IDs)
  const sendAllInvitesMutation = useMutation({
    mutationFn: async () => {
      if (!groupedCrewMembers || groupedCrewMembers.length === 0)
        return { successCount: 0, totalCount: 0 };

      const results = await Promise.allSettled(
        groupedCrewMembers.map((group) =>
          supabase.functions.invoke("send-crew-invite", {
            body: {
              crewMemberId: group.primaryRecord.id,
              briefingId: selectedBriefingId,
            },
          }),
        ),
      );

      const successCount = results.filter((r) => r.status === "fulfilled").length;
      return { successCount, totalCount: groupedCrewMembers.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["crew-members", selectedBriefingId] });
      toast({
        title: "Invitations sent",
        description: `${data.successCount} of ${data.totalCount} invitations sent`,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Something went wrong while sending the invitations",
        variant: "destructive",
      });
    },
  });

  const handleInvite = (crewMemberId: string) => {
    sendInviteMutation.mutate(crewMemberId);
  };

  const handleResendInvite = (crewMemberId: string) => {
    sendInviteMutation.mutate(crewMemberId);
  };

  const handleInviteAll = () => {
    sendAllInvitesMutation.mutate();
  };

  const getInviteStatus = (member: CrewMember) => {
    if (member.user_id) {
      return "account_created";
    } else if (member.email_opened_at) {
      return "mail_opened";
    } else if (member.email_delivered_at) {
      return "delivered";
    } else if (member.invited_at) {
      return "invitation_sent";
    }
    return "not_invited";
  };

  const confirmedCount = groupedCrewMembers.filter((g) => g.primaryRecord.user_id).length;
  const totalCount = groupedCrewMembers.length;

  return (
    <div className="flex min-h-screen bg-background">
      <ProductionSidebar productionId={selectedBriefingId!} />

      <PageTransition
        className="flex-1 transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" }}
      >
        <div className="border-b border-border bg-background px-6 py-4">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">Manage crew</h1>
              <p className="text-sm text-muted-foreground">View and manage your crew members</p>
            </div>
            {selectedBriefingId && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate(`/briefings/${selectedBriefingId}/edit?step=3&shortcut=true`)
                }
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit crew
              </Button>
            )}
          </div>
        </div>

        <div className="p-8">
          <div className="grid gap-8">
            {/* Crew List */}
            {selectedBriefingId && (
              <Card className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold">Crew members</h2>
                  </div>
                  {groupedCrewMembers.length > 0 && (
                    <div className="flex items-center gap-4">
                      <Button onClick={handleInviteAll} disabled={sendAllInvitesMutation.isPending}>
                        {sendAllInvitesMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Invite everyone
                          </>
                        )}
                      </Button>
                      {groupedCrewMembers.some((g) => g.primaryRecord.invited_at) && (
                        <span className="text-sm text-muted-foreground">
                          {confirmedCount} of {totalCount} crew members have created their account
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {crewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : groupedCrewMembers.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    No crew members added yet. Add crew members in the production briefing (Step 4).
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Function</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Event dates</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupedCrewMembers.map((group) => {
                        const member = group.primaryRecord;
                        const status = getInviteStatus(member);

                        const distinctRoles = Array.from(
                          new Set(group.rolesByDate.map((r) => (r.function ?? "").trim())),
                        );
                        const showsPerDayRoles = distinctRoles.length > 1;
                        return (
                          <TableRow key={member.email}>
                            <TableCell className="font-medium">{member.name}</TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>
                              {showsPerDayRoles ? (
                                <div className="space-y-0.5 text-sm">
                                  {group.rolesByDate.map((r) => (
                                    <div key={r.event_date} className="flex items-center gap-2">
                                      <span className="min-w-[60px] text-xs text-muted-foreground">
                                        {format(parseISO(r.event_date), "d MMM")}
                                      </span>
                                      <span>{r.function || "-"}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                member.function || "-"
                              )}
                            </TableCell>
                            <TableCell>{member.phone || "-"}</TableCell>
                            <TableCell>{formatEventDates(group.allDates)}</TableCell>
                            <TableCell>
                              {status === "account_created" ? (
                                <div className="flex items-center gap-2 text-green-600">
                                  <CheckCircle2 className="h-4 w-4" />
                                  <span className="text-sm font-medium">Account created</span>
                                </div>
                              ) : status === "mail_opened" ? (
                                <div className="flex items-center gap-2 text-blue-600">
                                  <Mail className="h-4 w-4" />
                                  <span className="text-sm font-medium">Mail opened</span>
                                </div>
                              ) : status === "delivered" ? (
                                <div className="flex items-center gap-2 text-purple-600">
                                  <CheckCircle2 className="h-4 w-4" />
                                  <span className="text-sm font-medium">Delivered</span>
                                </div>
                              ) : status === "invitation_sent" ? (
                                <div className="flex items-center gap-2 text-orange-600">
                                  <Mail className="h-4 w-4" />
                                  <span className="text-sm font-medium">Invitation sent</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  Not invited yet
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {status === "not_invited" ? (
                                  <Button
                                    onClick={() => handleInvite(member.id)}
                                    disabled={loadingInvites.has(member.id)}
                                    size="sm"
                                  >
                                    {loadingInvites.has(member.id) ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Invite
                                      </>
                                    )}
                                  </Button>
                                ) : status === "account_created" ? (
                                  <>
                                    <Button size="sm" variant="ghost" disabled>
                                      <CheckCircle2 className="mr-2 h-4 w-4" />
                                      Confirmed
                                    </Button>
                                    <Button
                                      onClick={() => handleInvite(member.id)}
                                      disabled={loadingInvites.has(member.id)}
                                      size="sm"
                                      variant="outline"
                                    >
                                      {loadingInvites.has(member.id) ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <>
                                          <Mail className="mr-2 h-4 w-4" />
                                          Invite
                                        </>
                                      )}
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    onClick={() => handleResendInvite(member.id)}
                                    disabled={loadingInvites.has(member.id)}
                                    size="sm"
                                    variant="outline"
                                  >
                                    {loadingInvites.has(member.id) ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Resend
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Card>
            )}
          </div>
        </div>
      </PageTransition>
      {selectedBriefingId && <EditProductionFAB briefingId={selectedBriefingId} />}
    </div>
  );
};

export default ManageCrew;
